import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Chip, ChipGroup } from '@/components/atoms/Chip';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { useAsyncData } from '@/hooks/useAsyncData';
import { categoriesAPI } from '@/services/api/categories.api';
import { preferencesAPI } from '@/services/api/preferences.api';
import { localizedField } from '@/utils/localizedField';
import type { Category } from '@/types/category.types';
import type { LearningPreferences } from '@/types/preferences.types';
import {
  MAX_CATEGORIES,
  MIN_CATEGORIES,
  type PreferencesSelectorHandle,
  type PreferencesSelectorProps,
} from './PreferencesSelector.types';
import styles from './PreferencesSelector.module.scss';

/**
 * UI de selección de categorías favoritas. Se usa tanto en
 * `OnboardingPreferencesPage` como en el slide 7 del welcome onboarding.
 *
 * Mantiene la selección internamente y la expone vía ref imperativa
 * (`getSelected`) para que el padre la persista cuando le toque (al
 * pulsar Guardar en la página, o al pulsar el arrow del dialog en el
 * slide). También notifica cambios de validez vía `onValidityChange`
 * para que el padre pueda habilitar/deshabilitar su botón.
 */
export const PreferencesSelector = forwardRef<
  PreferencesSelectorHandle,
  PreferencesSelectorProps
>(function PreferencesSelector(
  { disabled = false, errorMessage = null, onValidityChange },
  ref
) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);

  const categoriesQuery = useAsyncData<Category[]>({
    fetchFn: () => categoriesAPI.list(),
  });
  const currentPrefsQuery = useAsyncData<LearningPreferences | null>({
    fetchFn: async () => {
      const prefs = await preferencesAPI.get();
      return prefs.learningPreferences ?? null;
    },
  });

  // Precarga la selección con las categorías ya guardadas, si existen.
  useEffect(() => {
    if (currentPrefsQuery.data?.categorySlugs?.length) {
      setSelected(currentPrefsQuery.data.categorySlugs);
    }
  }, [currentPrefsQuery.data]);

  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data]
  );
  const isCountValid =
    selected.length >= MIN_CATEGORIES && selected.length <= MAX_CATEGORIES;
  const isInitialLoading =
    categoriesQuery.isLoading || currentPrefsQuery.isLoading;
  const hasCatalogError =
    !!categoriesQuery.error || (!isInitialLoading && categories.length === 0);

  // Notifica al padre cada cambio de validez (al inicio y cuando cambia
  // el set seleccionado). Útil para habilitar/deshabilitar el botón de
  // Guardar que vive en el padre.
  const lastNotifiedValidRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastNotifiedValidRef.current === isCountValid) return;
    lastNotifiedValidRef.current = isCountValid;
    onValidityChange?.(isCountValid);
  }, [isCountValid, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getSelected: () => selected,
      isValid: () => isCountValid,
    }),
    [selected, isCountValid]
  );

  const handleSelectionChange = (next: string[]) => {
    // Cap silencioso al máximo: si el alumno selecciona uno por encima,
    // no añadimos el extra en lugar de mostrar un error.
    if (next.length > MAX_CATEGORIES) return;
    setSelected(next);
  };

  if (isInitialLoading) {
    return (
      <div className={styles.selector__state}>
        <Spinner size="lg" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (hasCatalogError) {
    return (
      <div className={styles.selector__state}>
        <Icon icon={AlertCircle} size="lg" />
        <p className={styles.selector__error}>
          {t('onboardingPreferences.errorLoadingCatalog')}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.selector}>
      <ChipGroup
        selectionMode="multiple"
        value={selected}
        onValueChange={handleSelectionChange}
        aria-label={t('onboardingPreferences.chipsAriaLabel')}
        className={styles.selector__chips}
        disabled={disabled}
      >
        {categories.map((cat) => (
          <Chip key={cat.slug} value={cat.slug}>
            {localizedField(cat, 'name', i18n.language)}
          </Chip>
        ))}
      </ChipGroup>
      {errorMessage && (
        <p className={styles.selector__error} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
});

PreferencesSelector.displayName = 'PreferencesSelector';
